/**
 * Geospatial helpers shared across apps. Lives in `itinerary-types` (not
 * an app) so both `apps/api` and `apps/mobile` can import the same
 * great-circle math — the module-boundary rules forbid `apps/api` from
 * reaching into `apps/mobile`, and four hand-rolled haversine copies had
 * already started to drift (atan2 vs. asin forms, different call shapes).
 */

/** Minimal lat/lng pair. Structurally compatible with `Place`, the API's
 *  `LatLng`, and any `{ lat, lng }` object. */
export interface LatLngLiteral {
  lat: number;
  lng: number;
}

/** Mean Earth radius in metres (WGS-84 authalic sphere). */
const EARTH_RADIUS_M = 6_371_000;

/**
 * Great-circle (haversine) distance between two lat/lng pairs, in metres.
 * Symmetric: `haversineMeters(a, b) === haversineMeters(b, a)`.
 */
export function haversineMeters(from: LatLngLiteral, to: LatLngLiteral): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(to.lat - from.lat);
  const dLng = toRad(to.lng - from.lng);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_M * c;
}
